// src/layers/imagery/exploreRows.js
//
// Explore's time strip, as ImageryStrip rows (footprint restyle S-e slice 4a).
// Pure: everything here is a function of the catalog, the stack, the cursor,
// compare's state and the map center. components/imagery/ExploreTimeStrip.jsx
// wires it to the store and owns Play's timer and the message line.
//
// DB's answers (2026-11-03):
//   - one labeled captures row per series: the active stack's series, or every
//     entitled series when the stack is empty;
//   - coverage at the map CENTER (coverageAtPoint.js): none dims a chip ("no
//     coverage at map center"); unknown is drawn normally ("coverage unknown
//     here");
//   - Compare pairs within one series: the selected date and the nearest older
//     date covering the center (else the nearest newer);
//   - Sentinel-2 is a quiet row on the shared cursor, "Sentinel-2 · 10 m",
//     shown even with no captures; the chip drawn at the cursor is selected
//     (resolveReferenceLane: newest on or before it).
//
// S-e5 (DB, 2026-11-03): coverage is the ENTITLED area's. ExploreTimeStrip
// hands these the series through entitledCoverage.js withEntitledAreas, so
// every acquisition's geometry is its GET /imagery/entitled area, never the
// catalog's footprint (REM-692). A covered date the map is zoomed out past
// (below its minzoom) keeps full strength with the zoom-in mark, its tooltip
// "Zoom in to see this capture (from z<minzoom>)"; a click on it selects the
// date and zooms the map there. Sentinel-2's row is unchanged.
//
// Kept from TimeSlider: Latest is the newest date WITH COVERAGE at the center
// (never a Sentinel-2 year), Play steps over covered dates only, and the
// screen-reader status sentence (statusSentence) says what the map shows.
import { formatAcquisitionDate, formatReferenceDate, referenceWindow } from "./formatImageryDate";
import { coverageByDate, latestWithCoverage } from "./coverageAtPoint";
import { NO_COVERAGE_TEXT, zoomInText, zoomInTo } from "./entitledCoverage";
import { resolveReferenceLane } from "./resolveImageryStack";

export const REFERENCE_ROW_ID = "reference";
export const REFERENCE_ROW_LABEL = "Sentinel-2 · 10 m";
export const seriesRowId = (seriesKey) => `series:${seriesKey}`;
export const captureChipKey = (seriesKey, date) => `${seriesKey}@${date}`;
export const referenceChipKey = (date) => `reference@${date}`;

// ExploreTimeStrip's Play step and message lines (here, not in the component,
// so the component file exports only a component).
export const PLAY_INTERVAL_MS = 1500;
export const ALL_PINNED_TEXT = "All layers are pinned. Unpin one to play.";
export const noPairText = (seriesLabel) => `No other ${seriesLabel} date covers this view.`;

const COVERAGE = { true: "covered", false: "none", null: "unknown" };

/** The series the captures rows show: the stack's, in catalog order; every series when the stack is empty. */
export function captureSeries(catalog, stack) {
  const all = catalog?.series ?? [];
  if (!stack?.length) return all;
  const inStack = new Set(stack.map((e) => e.series_key));
  return all.filter((s) => inStack.has(s.series_key));
}

/** Each acquisition of a series at the center: { date, coverage, minzoom }, oldest first. */
export function seriesCoverage(series, center) {
  const byDate = center ? coverageByDate(series, center) : new Map();
  const minzoom = new Map((series.acquisitions ?? []).map((a) => [a.acquisition_date, a.minzoom]));
  return (series.acquisitions ?? [])
    .map((a) => a.acquisition_date)
    .filter(Boolean)
    .sort()
    .map((date) => ({ date, coverage: center ? COVERAGE[String(byDate.get(date) ?? null)] : "unknown", minzoom: minzoom.get(date) }));
}

/**
 * Sentinel-2's coverage at the center, per date, over the reference series as
 * ExploreTimeStrip hands them (geometries = the entitled answer's `reference`
 * areas, withEntitledAreas): covered if any composite of the date covers,
 * else unknown if any is unknown, else none; with the minzoom of one that covers.
 * @returns {Map<string, {coverage: "covered"|"unknown"|"none", minzoom: number|undefined}>}
 */
export function referenceCoverage(referenceList, center) {
  const out = new Map();
  const RANK = { covered: 2, unknown: 1, none: 0 };
  for (const s of referenceList ?? []) {
    for (const c of seriesCoverage(s, center)) {
      const cur = out.get(c.date);
      if (!cur || RANK[c.coverage] > RANK[cur.coverage]) out.set(c.date, { coverage: c.coverage, minzoom: c.minzoom });
    }
  }
  return out;
}

/** The newest date covering the center across these series, and its series. */
export function latestCovered(seriesList, center) {
  let best = { date: null, seriesKey: null };
  if (!center) return best;
  for (const s of seriesList) {
    const acq = latestWithCoverage(s, center);
    if (acq && (!best.date || acq.acquisition_date > best.date)) best = { date: acq.acquisition_date, seriesKey: s.series_key };
  }
  return best;
}

/** Every date covering the center across these series, oldest first, once each. */
export function playableDates(seriesList, center) {
  const dates = new Set();
  for (const s of seriesList) for (const c of seriesCoverage(s, center)) if (c.coverage === "covered") dates.add(c.date);
  return [...dates].sort();
}

/**
 * Compare's pair, within one series: the series holding the cursor's date
 * (else the first row's); its anchor the cursor's date if it covers the
 * center, else the series' newest covering date; B the anchor, A the nearest
 * older covering date (else the nearest newer, then A and B swap).
 * @returns {{series: object, pair: {seriesKey, a, b}|null}|null} null: no captures rows
 */
export function comparePairFor(seriesList, cursorDate, center) {
  if (!seriesList.length) return null;
  const series = seriesList.find((s) => (s.acquisitions ?? []).some((a) => a.acquisition_date === cursorDate)) ?? seriesList[0];
  const covered = seriesCoverage(series, center).filter((c) => c.coverage === "covered").map((c) => c.date);
  const anchor = covered.includes(cursorDate) ? cursorDate : covered[covered.length - 1];
  if (!anchor) return { series, pair: null };
  const i = covered.indexOf(anchor);
  const other = i > 0 ? covered[i - 1] : covered[i + 1];
  if (!other) return { series, pair: null };
  const [a, b] = other < anchor ? [other, anchor] : [anchor, other];
  return { series, pair: { seriesKey: series.series_key, a, b } };
}

/** Sentinel-2's dates, oldest first, and those drawn at the cursor. */
export function referenceLane(catalog, cursor) {
  const dates = new Set();
  for (const s of catalog?.reference ?? []) for (const a of s.acquisitions ?? []) if (a.acquisition_date) dates.add(a.acquisition_date);
  const showing = new Set(resolveReferenceLane({ cursor, catalog }).filter((sp) => sp.visible).map((sp) => sp.effective_date));
  return { dates: [...dates].sort(), showing };
}

/** The date a stack entry shows in compare, by its layer id. */
function compareMarks(stack, compareMode) {
  const marks = new Map();
  if (!compareMode?.enabled) return marks;
  for (const [id, side] of [[compareMode.left_layer_id, "A"], [compareMode.right_layer_id, "B"]]) {
    const e = stack.find((x) => x.layer_id === id);
    if (e?.pinned_date) marks.set(captureChipKey(e.series_key, e.pinned_date), side);
  }
  return marks;
}

function chipTitle(label, coverage, zoomIn) {
  if (coverage === "none") return `${label} — ${NO_COVERAGE_TEXT}`;
  if (coverage === "unknown") return `${label} — coverage unknown here`;
  if (zoomIn !== null) return `${label} — ${zoomInText(zoomIn)}`;
  return label;
}

/**
 * The strip's rows.
 * @param {object} p
 * @param {object[]} p.seriesList      captureSeries()
 * @param {object[]|null} [p.referenceList]  catalog.reference with the entitled areas (withEntitledAreas):
 *   the Sentinel-2 row's coverage (S-e5 check 7); null: no coverage fields on that row
 * @param {object} p.catalog
 * @param {object[]} p.stack
 * @param {object} p.cursor
 * @param {object} p.compareMode
 * @param {[number,number]|null} p.center
 * @param {number|null} [p.zoom]     the map's zoom: a covered date below its minzoom is marked zoom in (S-e5)
 * @param {boolean} p.playing
 * @param {import("react").ReactNode} [p.end]  Play, Compare, Latest: on the first captures row
 * @param {string|null} [p.message]   the first captures row's message line
 * @returns {object[]|null} ImageryStrip rows; null when there is nothing to show
 */
export function exploreRows({ seriesList, referenceList = null, catalog, stack, cursor, compareMode, center, zoom = null, playing, end = null, message = null }) {
  const cursorDate = cursor?.global_date ?? null;
  const marks = compareMarks(stack ?? [], compareMode);
  const rows = seriesList.map((s, i) => ({
    id: seriesRowId(s.series_key),
    label: s.display_name ?? s.series_key,
    tone: "primary",
    ...(i === 0 ? { end, message } : {}),
    chips: seriesCoverage(s, center).map(({ date, coverage, minzoom }) => {
      const key = captureChipKey(s.series_key, date);
      const label = formatAcquisitionDate(date);
      const selected = date === cursorDate;
      const zoomIn = zoomInTo({ minzoom }, coverage, zoom);
      return { key, label, title: chipTitle(label, coverage, zoomIn), selected, coverage, zoomIn,
        compare: marks.get(key), playing: Boolean(playing && selected) };
    }),
  }));
  const ref = referenceLane(catalog, cursor);
  const refCoverage = referenceList ? referenceCoverage(referenceList, center) : null;
  if (ref.dates.length) {
    rows.push({
      id: REFERENCE_ROW_ID,
      label: REFERENCE_ROW_LABEL,
      tone: "quiet",
      chips: ref.dates.map((date) => {
        const label = formatReferenceDate(date);
        const window = referenceWindow(date);
        const base = { key: referenceChipKey(date), label, selected: ref.showing.has(date),
          title: `${REFERENCE_ROW_LABEL} · ${window ?? label} · context only` };
        if (!refCoverage) return base;
        // A date with no composite in the list (none entitled anywhere) covers nothing.
        const { coverage, minzoom } = refCoverage.get(date) ?? { coverage: center ? "none" : "unknown" };
        const zoomIn = zoomInTo({ minzoom }, coverage, zoom);
        return { ...base, coverage, zoomIn, title: chipTitle(base.title, coverage, zoomIn) };
      }),
    });
  }
  return rows.length ? rows : null;
}

/**
 * What the map shows, in one sentence, for screen readers (TimeSlider's,
 * kept: DB's answer 7). Availability and display are different sentences.
 */
export function statusSentence({ seriesList, cursorDate, center, latestDate, rendering }) {
  if (!center) return "Locating map view…";
  const selectedSeries = seriesList.find((s) => (s.acquisitions ?? []).some((a) => a.acquisition_date === cursorDate));
  const label = selectedSeries ? formatAcquisitionDate(cursorDate) : null;
  let coverage;
  if (selectedSeries) {
    // A date covers if ANY series offering it covers; unknown only if none knew.
    const values = seriesList.map((s) => seriesCoverage(s, center).find((c) => c.date === cursorDate)?.coverage).filter(Boolean);
    coverage = values.includes("covered") ? "covered" : values.includes("unknown") ? "unknown" : "none";
  }
  if (!latestDate && !selectedSeries) return "No imagery for this location";
  if (selectedSeries && coverage === "none") return `No imagery here for ${label}`;
  if (selectedSeries && coverage === "unknown") return `Coverage unknown for ${label}`;
  if (!rendering.any) return `${label ?? formatAcquisitionDate(latestDate)} has imagery here — select a date to show it`;
  if (selectedSeries && !rendering.dates.has(cursorDate)) return `${label} has imagery here — the active layer is pinned to another date`;
  const d = label ?? formatAcquisitionDate(latestDate);
  return cursorDate === latestDate || !selectedSeries ? `Showing ${d} — the most recent imagery here` : `Showing ${d}`;
}
